import React, {Component, forwardRef} from 'react'
import Select, {components} from 'react-select'
import Typography from '@mui/material/Typography'
import NoSsr from '@mui/material/NoSsr'
import TextField from '@mui/material/TextField'
import Paper from '@mui/material/Paper'
import MenuItem from '@mui/material/MenuItem'
import {Trans, withNamespaces} from 'react-i18next'
import withStyles from '@mui/styles/withStyles'
import messages from '../../assets/messages'
import {find, flatten, get, flowRight as compose, lowerCase, includes} from 'lodash'
import FlagIcon from '../FlagIcon'
import {countries} from '@bdswiss/common-enums'

const styles = theme => ({
  input: {
    display: 'flex',
    padding: 0,
  },
  valueContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    flex: 1,
    overflow: 'hidden',
    cursor: 'pointer'
  },
  noOptionsMessage: {
    padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
  },
  singleValue: {
    fontSize: 16,
    marginBottom: -3,
    marginTop: -1,
    [theme.breakpoints.down('md')]: {
      marginTop: 7,
    },
  },
  placeholder: {
    position: 'absolute',
    bottom: 1,
    color: theme.palette.grey[500],
    [theme.breakpoints.down('md')]: {
      bottom: 10,
    },
  },
  paper: {
    position: 'absolute',
    zIndex: 1,
    marginTop: theme.spacing(1),
    left: 0,
    right: 0,
    backgroundColor: theme.palette.background.paper,
    boxShadow: 'none',
  },
  red: {
    color: theme.palette.red.color,
    position: 'absolute',
    bottom: 1,
  },
  fontLight:{
    fontWeight: 300
  },
  flagItem: {
    [theme.direction === 'rtl' ? 'marginLeft' : 'marginRight']: 10,
  },
  inputComponent:{
    color: theme.palette.grey.color
  },
  menuItemRoot: {
    paddingTop: 10,
    paddingBottom: 10
  },
})

const InputComponent = forwardRef((props, ref) => <div ref={ref} {...props} />)

function Control(props) {
  return (
    <TextField
      variant="standard"
      fullWidth
      inputRef={props.ref}
      InputProps={{
        inputComponent: InputComponent,
        inputProps: {
          className: props.selectProps.classes.input,
          children: props.children,
          ...props.innerProps,
        },
      }}
      {...props.selectProps.textFieldProps} />
  )
}

function Option(props) {
  return (
    <MenuItem
      selected={props.isFocused}
      component="div"
      style={{
        fontWeight: props.isSelected ? 500 : 400,
      }}
      classes={{root: props.selectProps.classes.menuItemRoot}}
      {...props.innerProps}
    >
      <FlagIcon
        code={get(props, 'value').toLowerCase()}
        className={props.selectProps.classes.flagItem}
        size={'lg'}
      />
      {props.children}
    </MenuItem>
  )
}

function Placeholder(props) {
  return (
    <Typography
      className={(props.selectProps.errors[props.selectProps.name])?props.selectProps.classes.red:props.selectProps.classes.placeholder}
      {...props.innerProps}
      variant="body2"
    >
      {props.children}
    </Typography>
  )
}

function SingleValue(props) {
  return (
    <Typography className={props.selectProps.classes.singleValue} {...props.innerProps} variant="body2">
      {props.data && props.data.value && <FlagIcon
        code={props.data.value.toLowerCase()}
        className={props.selectProps.classes.flagItem}
        size={'lg'}
      />}
      {props.children}
    </Typography>
  )
}

function ValueContainer(props) {
  return <div className={props.selectProps.classes.valueContainer}>
    {props.data && props.data.value && <FlagIcon
      code={props.data.value.toLowerCase()}
      className={props.selectProps.classes.flagItem}
      size={'lg'}
    />}
    {props.children}
  </div>
}

function Menu(props) {
  return (
    <Paper square className={props.selectProps.classes.paper} {...props.innerProps}>
      {props.children}
    </Paper>
  )
}

function NoOptionsMessage(props) {
  return (
    <Typography
      color="textSecondary"
      className={props.selectProps.classes.noOptionsMessage}
      {...props.innerProps}
      variant="body2"
    >
      {<Trans {...messages.noOptions}/>}
    </Typography>
  )
}

const Input = ({autoComplete, ...props}) => <components.Input {...props} autoComplete="none" className={props.selectProps.classes.inputComponent} />

export class CountrySelect extends Component {

  findValue(options, value, name, prefixValue) {
    const country = countries[lowerCase(value)] || get(find(countries, (c) => c.value === value), 'key')
    if (includes(lowerCase(name), 'prefix')) {
      return find(options,(option) => (value && country.callingCodes) ? option.value === value && option.label === prefixValue : option.value === value)
    } else {
      return find(options,(option) => option.label === value)
    }
  }

  render() {
    const {classes, countryList, label, errors, value, name, phonePrefix, returnKey, prefixValue, showRequired} = this.props
    const suggestions = countryList
    const options = flatten(suggestions.map(suggestion => (phonePrefix && suggestion.callingCodes) ?
      suggestion.callingCodes.map((code) => ({
        value:  suggestion.value,
        label: `+${code}`,
        key: suggestion.key,
      }))
      : {
        value:  suggestion.value,
        label: (!phonePrefix) ? get(suggestion, 'nationality') || get(suggestion, 'label') :`+${suggestion.callingCode}`,
        key: suggestion.key,
      }))

    const components = {
      Control,
      Menu,
      NoOptionsMessage,
      Option,
      Placeholder,
      SingleValue,
      ValueContainer,
      Input
    }

    return <div className={classes.root}>
      <NoSsr>
        <Select
          countries={countryList}
          classes={classes}
          options={options}
          components={components}
          value={this.findValue(options,value, name, prefixValue) || ''}
          onChange={(e) => {
            this.props.handleChangeField(e.value)
            this.props.handleChange(name,(!phonePrefix)? ((returnKey) ? e.key : e.value ):e.label)
            this.props.setStateOuter(`${name}Changed`,true)
          }}
          placeholder={(value)?'':`${label} ${showRequired ? '*' : ''}`}
          textFieldProps={{
            label: (!value)?' ':`${label} ${showRequired ? '*' : ''}`,
            InputLabelProps: {
              shrink: true
            },
            error:errors[name]
          }}
          errors={errors}
          name={name}
          isDisabled={this.props.disabled}
        />
      </NoSsr>
    </div>
  }
}

export default compose(withStyles(styles, {withTheme: true}), withNamespaces())(CountrySelect)
